
import { defineComponent, PropType } from 'vue';
import { store } from '@/store';
import { addUserToGroup, type Group } from '@training24/organisation/src/group';
import { adminUpdateProfile } from '@training24/user/src/admin';

interface GroupNode extends Group {
  children: GroupNode[];
}

export default defineComponent({
  props: {
    groups: {
      type: Array as PropType<Group[]>,
      default: (): Group[] => [],
    },
  },

  emits: ['user-created'],

  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      groupId: '',
      isAdmin: false,
      sendInvitation: true,
      consentGiven: false,
      loading: false,
      success: false,
      errorMsg: '',
      savedName: '',
      invitationSentTo: '',
    };
  },

  computed: {
    /** Baumstruktur: Wurzeln → Kinder → Enkel */
    groupTree(): GroupNode[] {
      const byParent = (parentId: string | null | undefined): GroupNode[] =>
        (this.groups as Group[])
          .filter((g) => (g.parentGroupId ?? null) === (parentId ?? null))
          .sort((a, b) => a.name.localeCompare(b.name, 'de'))
          .map((g) => ({ ...g, children: byParent(g.groupId) }));
      return byParent(null);
    },
    flatGroupOptions(): Group[] {
      return this.groups;
    },
  },

  methods: {
    async save() {
      if (!this.firstName || !this.lastName || !this.email) return;
      this.loading = true;
      this.errorMsg = '';
      try {
        const created = await store.dispatch('permission/ADD', {
          email: this.email,
          userName: `${this.firstName}.${this.lastName}`,
          firstName: this.firstName,
          lastName: this.lastName,
          type: this.isAdmin ? 'ADMIN' : 'VIEWER',
          // Einladungs-Mail (Admins: Administrator-Vorlage, sonst Willkommens-Mail)
          sendInivtiationEmail: this.sendInvitation,
          consentGiven: this.consentGiven,
        });

        if (created?.userId) {
          const orgId = store.getters['organisation/getOrganisationId'];
          const jwt = store.getters['getJWT'];

          // Vor-/Nachname im Profil ablegen (gleiche Quelle wie das Bearbeiten-Modal),
          // damit der Name direkt nach dem Anlegen überall korrekt erscheint.
          try {
            await adminUpdateProfile(orgId, created.userId, {
              first_name: this.firstName,
              last_name: this.lastName,
              consentGiven: this.consentGiven,
            } as any, jwt);
          } catch {
            // Profil-Update fehlgeschlagen — User wurde trotzdem angelegt
          }

          // Wenn eine Gruppe gewählt wurde, Mitarbeiter eintragen
          if (this.groupId) {
            try {
              await addUserToGroup(orgId, this.groupId, created.userId, jwt);
            } catch {
              // Gruppen-Zuweisung fehlgeschlagen — User wurde trotzdem angelegt
            }
          }
        }

        this.savedName = `${this.firstName} ${this.lastName}`;
        this.invitationSentTo = this.sendInvitation ? this.email : '';
        this.success = true;
        this.$emit('user-created');
      } catch (e: any) {
        const status = e?.response?.status;
        const code = e?.response?.data?.e?.code;
        if (status === 409) {
          this.errorMsg = 'Diese Person ist dieser Einrichtung bereits zugeordnet.';
        } else if (code === 'UsernameExistsException') {
          this.errorMsg = 'Ein Benutzer mit diesem Namen existiert bereits.';
        } else {
          this.errorMsg = e?.response?.data?.e?.message || 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.';
        }
      } finally {
        this.loading = false;
      }
    },

    reset() {
      this.firstName = '';
      this.lastName = '';
      this.email = '';
      this.groupId = '';
      this.isAdmin = false;
      this.sendInvitation = true;
      this.consentGiven = false;
      this.loading = false;
      this.success = false;
      this.errorMsg = '';
      this.savedName = '';
      this.invitationSentTo = '';
    },
  },

  mounted() {
    const modal = document.getElementById('inviteUser');
    modal?.addEventListener('show.bs.modal', () => this.reset());
  },
});
